import {
  Flame,
  Camera,
  ScanBarcode,
  Dumbbell,
  Droplets,
  CookingPot,
  TrendingUp,
  Smartphone,
  Component,
  MapPinCheck,
} from "lucide-react";
import AnimatedCard from "./AnimatedCard";
import Seo, { PLAY_STORE_URL } from "../Seo";

const TITLE = "ThriveOn — AI Calorie Tracker & Food Scanner App";
const DESCRIPTION =
  "Free AI calorie tracker for Android. Scan meals with your camera, count calories by barcode, log workouts and track your macros. Get ThriveOn on Google Play.";

export default function LandingPage() {
  return (
    <div className="landing-page-root">
      <Seo title={TITLE} description={DESCRIPTION} path="/" />

      <header className="landing-page-header">
        <div className="logo-container">
          <img src="/assets/logo_transparent.png" alt="ThriveOn logo" />
          <div>
            <h2>Thriveon</h2>
            <p>Track. Grow. Thriveon.</p>
          </div>
        </div>
        <nav aria-label="Main">
          <a href="#preview-section">Preview</a>
          <a href="#features-section">Features</a>
          <a href="#devices-section">Devices</a>
          <a href="#faq-section">FAQ</a>
        </nav>
      </header>

      <main className="landing-page-main">
        <section className="section-1" id="preview-section">
          <div className="hint">
            <Flame aria-hidden="true" />
            The first<span>100% free</span>Calorie Tracker App
          </div>

          <div>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Download ThriveOn Calorie Tracker on Google Play"
            >
              <img
                style={{
                  width: 150,
                  backgroundColor: "white",
                  borderRadius: 10,
                  cursor: "pointer",
                }}
                src="/assets/playstore_badge.png"
                alt="Get ThriveOn Calorie Tracker on Google Play"
              />
            </a>
          </div>

          <h1>
            AI Calorie Tracker <span>&amp; Food Scanner</span>
          </h1>
          <p>
            Snap a photo of your meal to count calories, scan barcodes, track
            protein, carbs and fats, and log your workouts — free on Android.
          </p>
          <div className="img-container">
            <img
              src="/assets/dashboard.png"
              alt="ThriveOn dashboard showing daily calories, macro breakdown and water intake"
            />
          </div>
        </section>

        <section className="section-2" id="features-section">
          <h2>
            Everything you need <span>to succeed</span>
          </h2>
          <p>
            Comprehensive tools designed to help you reach your health goals
            faster
          </p>
          <div className="cards-container">
            {features.map((feature) => (
              <AnimatedCard key={feature.title} feature={feature} />
            ))}
          </div>
        </section>

        <section className="section-3" id="devices-section">
          <h2>
            Track <span> anywhere, anytime</span>
          </h2>
          <div className="flex-row-container">
            <div className="img-container">
              <img
                src="/assets/mobile_dashboard.png"
                alt="ThriveOn calorie tracker running on an Android phone"
                loading="lazy"
              />
            </div>
            <div className="features-container">
              <div className="feature">
                <Smartphone aria-hidden="true" />
                <div>
                  <h3>Mobile First Design</h3>
                  <p>Intuitive touch interface designed for mobile devices</p>
                </div>
              </div>
              <div className="feature">
                <Component aria-hidden="true" />
                <div>
                  <h3>Fast &amp; Smooth</h3>
                  <p>Lightning-fast performance on all devices</p>
                </div>
              </div>
              <div className="feature">
                <MapPinCheck aria-hidden="true" />
                <div>
                  <h3>Track your meals from anywhere</h3>
                  <p>Log your meals and track your macros anywhere you are</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/*
          These questions are duplicated as FAQPage structured data in
          public/index.html. Google requires the markup and the visible text to
          match — if you edit an answer here, edit it there too.
        */}
        <section className="section-faq" id="faq-section">
          <h2>
            Frequently asked <span>questions</span>
          </h2>
          <div className="faq-list">
            {faqs.map((faq) => (
              <details className="faq-item" key={faq.question}>
                <summary>
                  <h3>{faq.question}</h3>
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>

          <a className="faq-cta" href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
            Download ThriveOn on Google Play
          </a>
        </section>
      </main>

      <footer className="landing-page-footer">
        <div className="logo-container">
          <img src="/assets/logo_transparent.png" alt="" />
          <h2>ThriveOn</h2>
        </div>
        <div>
          <p>© 2025 ThriveOn. Track your wellness journey.</p>
          <a href="/privacy-policy">Privacy policy</a>
        </div>
      </footer>
    </div>
  );
}

const features = [
  {
    icon: Camera,
    title: "AI food scanner",
    description:
      "Take a photo of your plate and ThriveOn recognises the food and fills in the calories and macros for you",
    backgroundColor: "rgba(249, 116, 21, 0.2)",
    mainColor: "rgba(249, 116, 21, 1)",
    speed: 0.1,
    angle: 0,
  },
  {
    icon: ScanBarcode,
    title: "Barcode scanner",
    description:
      "Scan any packaged product to pull its nutrition information straight off the label — no typing",
    backgroundColor: "rgba(177, 82, 224, 0.2)",
    mainColor: "rgba(177, 82, 224, 1)",
    speed: 0.2,
    angle: 150,
  },
  {
    icon: TrendingUp,
    title: "Macro tracking",
    description:
      "Monitor your protein, carbs and fats against daily targets with beautiful visual breakdowns",
    backgroundColor: "rgba(37, 228, 228, 0.2)",
    mainColor: "rgba(37, 228, 228, 1)",
    speed: 0.25,
    angle: 120,
  },
  {
    icon: Dumbbell,
    title: "Workout logging",
    description:
      "Log training from a built-in exercise catalogue and count calories burned alongside calories eaten",
    backgroundColor: "rgba(33, 196, 93, 0.2)",
    mainColor: "rgba(33, 196, 93, 1)",
    speed: 0.35,
    angle: 80,
  },
  {
    icon: CookingPot,
    title: "Recipes & custom foods",
    description:
      "Save your own meals and recipes once, then log them again in a single tap",
    backgroundColor: "rgba(245, 159, 10, 0.2)",
    mainColor: "rgba(245, 159, 10, 1)",
    speed: 0.3,
    angle: 60,
  },
  {
    icon: Droplets,
    title: "Water & weight tracking",
    description:
      "Keep an eye on hydration and watch your weight trend over time on a single dashboard",
    backgroundColor: "rgba(220, 40, 40, 0.2)",
    mainColor: "rgba(220, 40, 40, 1)",
    speed: 0.4,
    angle: 30,
  },
];

const faqs = [
  {
    question: "Is ThriveOn free?",
    answer:
      "Yes. Calorie tracking, macro tracking, barcode scanning, workout logging and recipes are all free. An optional premium upgrade raises the daily limit on AI photo scans.",
  },
  {
    question: "How does the AI food scanner count calories from a photo?",
    answer:
      "Point your camera at a meal and take a photo. ThriveOn recognises the foods on the plate, estimates the portion size, and fills in calories, protein, carbs and fats so you can log the meal in one tap. You can edit anything before saving.",
  },
  {
    question: "Can I scan barcodes on packaged food?",
    answer:
      "Yes. Scan the barcode on any packaged product and ThriveOn pulls the nutrition information straight from the label, so you do not have to type anything in.",
  },
  {
    question: "Does ThriveOn track macros as well as calories?",
    answer:
      "Yes. Every meal you log breaks down into protein, carbs and fats, with daily targets calculated from your goal, and visual charts showing how the day is going.",
  },
  {
    question: "Is ThriveOn available on iPhone?",
    answer:
      "ThriveOn is available on Android through Google Play. An iOS version is in development.",
  },
  {
    question: "Can I log workouts and exercise?",
    answer:
      "Yes. ThriveOn includes a workout log with an exercise catalogue, so calories burned from training are counted alongside the calories you eat.",
  },
];